import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { resolveMediaUrl } from '../Common/DetailParts';
import { memberApplicationAPI } from '../../../services/membershipApi';
import { parseNIC } from '../../../utils/nicUtils';
import toast, { Toaster } from 'react-hot-toast';
import { RequirementOptionsPanel, requirementsMessage } from '../Common/requirements';

const isProductionBuild = ['production', 'prod'].includes(String(import.meta.env.VITE_APP_ENV || '').toLowerCase());
const LOGO_SRC = isProductionBuild ? '/logo-slim-live.png' : '/logo-slim-h2bis.png';

const Field = ({ label, required, children, span }) => (
  <div className={span ? 'sm:col-span-2' : ''}>
    <label className="mb-1 block text-sm font-medium text-gray-700">
      {label}{required && <span className="ml-0.5 text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const inputClass = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-violet-500';

/** Initials + First Name + Last Name, e.g. "A.B.C. John Perera". */
function computeNameWithInitials(initials, firstName, lastName) {
  return [(initials || '').trim(), (firstName || '').trim(), (lastName || '').trim()].filter(Boolean).join(' ');
}

// Defined at module scope (not inside the form component) so its identity stays stable across
// re-renders. Defining it inside the component recreated it on every keystroke, which made React
// remount the whole subtree under it and drop focus out of the field being typed in.
const Shell = ({ children }) => (
  <div className="min-h-screen bg-gray-50 px-4 py-10 sm:px-6 lg:px-8">
    <Toaster />
    <div className="mx-auto w-full max-w-3xl">
      <div className="mb-8 text-center">
        <img src={LOGO_SRC} alt="Sri Lanka Institute of Marketing" className="mx-auto h-14 w-auto" />
        <h1 className="mt-4 text-xl font-medium text-gray-900">Sri Lanka Institute of Marketing</h1>
        <p className="mt-1 text-sm text-gray-500">Membership Application</p>
      </div>
      {children}
    </div>
  </div>
);

const PublicMembershipApplicationForm = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [alreadySubmitted, setAlreadySubmitted] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [slotUploading, setSlotUploading] = useState({});

  const [invite, setInvite] = useState(null); // { applicationNumber, email, membershipTypeName, requiredDocuments }
  // Tracks whether "Name with Initials" has been typed into directly, so the auto-fill effect
  // below never clobbers a deliberate value.
  const nameWithInitialsTouched = useRef(false);
  const [form, setForm] = useState({
    title: '', firstName: '', lastName: '', initials: '', nameWithInitials: '',
    phone: '', mobilePhone: '', homePhone: '', officePhone: '',
    nic: '', passportNumber: '', tinNumber: '', dob: '', gender: '',
    company: '', designation: '', companyEmail: '', qualifications: '', notes: '',
    isPreviousMember: false, manualMemberId: '',
    address: { line1: '', line2: '', line3: '', town: '', city: '', district: '', province: '', postalCode: '', country: 'Sri Lanka' },
    profilePicture: '',
    documents: [],
  });

  useEffect(() => {
    memberApplicationAPI.getPublicApplicationForm(token)
      .then((res) => {
        const data = res.data?.data;
        if (data?.alreadySubmitted) {
          setAlreadySubmitted(data);
          return;
        }
        setInvite(data);
        setForm((prev) => ({ ...prev, mobilePhone: data?.mobilePhone || '' }));
      })
      .catch((err) => setError(err.response?.data?.message || 'Invalid or expired application link.'))
      .finally(() => setLoading(false));
  }, [token]);

  // Keeps "Name with Initials" in sync with Initials / First Name / Last Name until the
  // applicant types into that field directly — see the ref above.
  useEffect(() => {
    if (nameWithInitialsTouched.current) return;
    const auto = computeNameWithInitials(form.initials, form.firstName, form.lastName);
    if (auto) setForm((prev) => (prev.nameWithInitials === auto ? prev : { ...prev, nameWithInitials: auto }));
  }, [form.initials, form.firstName, form.lastName]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleNICChange = (e) => {
    const nic = e.target.value;
    const parsed = parseNIC(nic);
    setForm((prev) => ({ ...prev, nic, ...(parsed ? { dob: parsed.dob, gender: parsed.gender } : {}) }));
  };

  const handleSlotUpload = async (docName, e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    setSlotUploading((p) => ({ ...p, [docName]: true }));
    try {
      const res = await memberApplicationAPI.upload(formData);
      set('documents', [...form.documents.filter((d) => d.name !== docName), { name: docName, url: res.data.url }]);
      toast.success(`"${docName}" uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setSlotUploading((p) => ({ ...p, [docName]: false }));
    }
  };

  const removeDoc = (docName) => set('documents', form.documents.filter((d) => d.name !== docName));

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      const res = await memberApplicationAPI.upload(formData);
      set('profilePicture', res.data.url);
      toast.success('Profile picture uploaded');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Any one requirement option fully uploaded is enough (or every document, for a type without options)
    const docProblem = requirementsMessage(invite, form.documents.map((d) => d.name));
    if (docProblem) {
      toast.error(docProblem, { duration: 7000 });
      return;
    }

    setSubmitting(true);
    try {
      await memberApplicationAPI.submitPublicApplicationForm(token, {
        title: form.title,
        firstName: form.firstName,
        lastName: form.lastName,
        initials: form.initials,
        nameWithInitials: form.nameWithInitials,
        gender: form.gender,
        phone: form.phone,
        mobilePhone: form.mobilePhone,
        homePhone: form.homePhone,
        officePhone: form.officePhone,
        nic: form.nic,
        passportNumber: form.passportNumber,
        tinNumber: form.tinNumber,
        dateOfBirth: form.dob,
        organization: form.company,
        occupation: form.designation,
        companyEmail: form.companyEmail,
        isPreviousMember: form.isPreviousMember,
        manualMemberId: form.isPreviousMember ? form.manualMemberId : '',
        professionalInfo: { employer: form.company, designation: form.designation, officeEmail: form.companyEmail },
        address: form.address,
        profilePicture: form.profilePicture,
        documents: form.documents,
        qualifications: form.qualifications,
        notes: form.notes,
      });
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-sm text-gray-500 shadow-sm">
          Loading your application
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <h2 className="text-lg font-medium text-gray-900">Link invalid or expired</h2>
          <p className="mt-2 text-sm text-gray-500">{error}</p>
          <p className="mt-4 text-xs text-gray-400">Please contact the SLIM membership team to request a new link.</p>
        </div>
      </Shell>
    );
  }

  if (alreadySubmitted) {
    return (
      <Shell>
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <h2 className="text-lg font-medium text-gray-900">Application already submitted</h2>
          <p className="mt-2 text-sm text-gray-500">
            This application ({alreadySubmitted.applicationNumber}) has already been submitted and is being processed.
          </p>
        </div>
      </Shell>
    );
  }

  if (success) {
    return (
      <Shell>
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <h2 className="text-lg font-medium text-gray-900">Application submitted</h2>
          <p className="mt-2 text-sm text-gray-500">
            Thank you. Your membership application has been submitted and is now awaiting review by our membership team.
          </p>
          <p className="mt-4 text-xs text-gray-400">Application number: {invite?.applicationNumber}</p>
        </div>
      </Shell>
    );
  }

  const requiredDocs = invite?.requiredDocuments || [];

  return (
    <Shell>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-4">
            <div>
              <p className="text-xs text-gray-500">Application Number</p>
              <p className="text-sm font-medium text-gray-900">{invite?.applicationNumber}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Membership Category</p>
              <p className="text-sm font-medium text-gray-900">{invite?.membershipTypeName}</p>
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-600">
            Please complete your details below. Fields marked with an asterisk are required.
          </p>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="text-base font-medium text-gray-900">Applicant Information</h3>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Title" required>
              <select className={inputClass} value={form.title} onChange={(e) => set('title', e.target.value)} required>
                <option value="">Select</option>
                <option>Mr</option><option>Ms</option><option>Mrs</option><option>Dr</option><option>Prof</option><option>Rev</option><option>Other</option>
              </select>
            </Field>
            <Field label="Initials"><input className={inputClass} value={form.initials} onChange={(e) => set('initials', e.target.value)} placeholder="e.g. A.B.C." /></Field>
            <Field label="First Name" required><input className={inputClass} value={form.firstName} onChange={(e) => set('firstName', e.target.value)} required /></Field>
            <Field label="Last Name" required><input className={inputClass} value={form.lastName} onChange={(e) => set('lastName', e.target.value)} required /></Field>
            <Field label="Name with Initials" required span>
              <input className={inputClass} value={form.nameWithInitials} onChange={(e) => { nameWithInitialsTouched.current = true; set('nameWithInitials', e.target.value); }} required placeholder="e.g. A.B.C. John Perera" />
              <p className="mt-1 text-xs text-gray-400">Filled in automatically from Initials, First Name and Last Name — edit it if you need something different.</p>
            </Field>
            <Field label="Email">
              <input className={`${inputClass} bg-gray-50 text-gray-500`} value={invite?.email || ''} disabled />
            </Field>
            <Field label="NIC" required><input className={inputClass} value={form.nic} onChange={handleNICChange} required /></Field>
            <Field label="Mobile Phone" required><input className={inputClass} value={form.mobilePhone} onChange={(e) => set('mobilePhone', e.target.value)} required /></Field>
            <Field label="Home/Office Phone"><input className={inputClass} value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
            <Field label="Date of Birth"><input type="date" className={inputClass} value={form.dob} onChange={(e) => set('dob', e.target.value)} /></Field>
            <Field label="Gender">
              <select className={inputClass} value={form.gender} onChange={(e) => set('gender', e.target.value)}>
                <option value="">Select</option>
                <option>Male</option><option>Female</option><option>Other</option>
              </select>
            </Field>
            <Field label="TIN Number"><input className={inputClass} value={form.tinNumber} onChange={(e) => set('tinNumber', e.target.value)} placeholder="Tax Identification Number (if any)" /></Field>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="text-base font-medium text-gray-900">Residential Address</h3>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Address Line 1" required span><input className={inputClass} value={form.address.line1} onChange={(e) => set('address', { ...form.address, line1: e.target.value })} required /></Field>
            <Field label="Address Line 2"><input className={inputClass} value={form.address.line2} onChange={(e) => set('address', { ...form.address, line2: e.target.value })} /></Field>
            <Field label="Address Line 3"><input className={inputClass} value={form.address.line3} onChange={(e) => set('address', { ...form.address, line3: e.target.value })} /></Field>
            <Field label="Town/City" required><input className={inputClass} value={form.address.city} onChange={(e) => set('address', { ...form.address, city: e.target.value })} required /></Field>
            <Field label="District"><input className={inputClass} value={form.address.district} onChange={(e) => set('address', { ...form.address, district: e.target.value })} /></Field>
            <Field label="Province"><input className={inputClass} value={form.address.province} onChange={(e) => set('address', { ...form.address, province: e.target.value })} /></Field>
            <Field label="Postal Code"><input className={inputClass} value={form.address.postalCode} onChange={(e) => set('address', { ...form.address, postalCode: e.target.value })} /></Field>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="text-base font-medium text-gray-900">Company and Designation</h3>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Are you previously a member?">
              <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
                {[{ value: false, label: 'No' }, { value: true, label: 'Yes' }].map((opt) => (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, isPreviousMember: opt.value, manualMemberId: opt.value ? p.manualMemberId : '' }))}
                    className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${form.isPreviousMember === opt.value ? 'bg-white text-violet-700 shadow-sm ring-1 ring-black/5' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </Field>
            {form.isPreviousMember ? (
              <Field label="Old Membership Number"><input className={inputClass} value={form.manualMemberId} onChange={(e) => set('manualMemberId', e.target.value)} /></Field>
            ) : <div className="hidden sm:block" />}
            <Field label="Company Name"><input className={inputClass} value={form.company} onChange={(e) => set('company', e.target.value)} /></Field>
            <Field label="Designation"><input className={inputClass} value={form.designation} onChange={(e) => set('designation', e.target.value)} /></Field>
            <Field label="Company Email" span><input type="email" className={inputClass} value={form.companyEmail} onChange={(e) => set('companyEmail', e.target.value)} /></Field>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="text-base font-medium text-gray-900">Required Documents</h3>
          <p className="mt-1 text-sm text-gray-500">Please upload clear PDF, JPG or PNG copies (max 5MB each).</p>

          <div className={`mt-4 flex items-center justify-between rounded-lg border p-4 ${form.profilePicture ? 'border-emerald-200 bg-emerald-50' : 'border-dashed border-gray-200 bg-gray-50'}`}>
            <div className="flex min-w-0 items-center gap-3">
              {form.profilePicture ? (
                <img src={resolveMediaUrl(form.profilePicture)} alt="Profile" className="h-12 w-12 shrink-0 rounded-full border border-emerald-300 object-cover" />
              ) : (
                <div className="h-12 w-12 shrink-0 rounded-full bg-gray-200" />
              )}
              <div className="min-w-0">
                <p className={`truncate text-sm font-medium ${form.profilePicture ? 'text-emerald-800' : 'text-gray-700'}`}>Profile Picture</p>
                <p className={`text-xs ${form.profilePicture ? 'text-emerald-600' : 'text-gray-400'}`}>{form.profilePicture ? 'Uploaded' : 'Pending upload'}</p>
              </div>
            </div>
            <label className={`shrink-0 cursor-pointer rounded-lg px-3 py-1.5 text-xs font-medium ${form.profilePicture ? 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50' : 'bg-violet-600 text-white hover:bg-violet-700'}`}>
              {form.profilePicture ? 'Replace' : 'Upload'}
              <input type="file" className="hidden" accept="image/*" onChange={handleProfilePictureUpload} disabled={uploading} />
            </label>
          </div>

          <RequirementOptionsPanel type={invite} providedNames={form.documents.map((d) => d.name)} className="mt-4" />
          {requiredDocs.length > 0 ? (
            <div className="mt-3 space-y-3">
              {requiredDocs.map((docName) => {
                const uploaded = form.documents.find((d) => d.name === docName);
                const isUploading = !!slotUploading[docName];
                return (
                  <div key={docName} className={`flex items-center justify-between rounded-lg border p-4 ${uploaded ? 'border-emerald-200 bg-emerald-50' : 'border-dashed border-gray-200 bg-gray-50'}`}>
                    <div className="min-w-0">
                      <p className={`truncate text-sm font-medium ${uploaded ? 'text-emerald-800' : 'text-gray-700'}`}>{docName}</p>
                      <p className={`text-xs ${uploaded ? 'text-emerald-600' : 'text-gray-400'}`}>{uploaded ? 'Uploaded' : 'Pending upload'}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {uploaded && (
                        <button type="button" onClick={() => removeDoc(docName)} className="text-xs font-medium text-rose-600 hover:text-rose-700">Remove</button>
                      )}
                      <label className={`cursor-pointer rounded-lg px-3 py-1.5 text-xs font-medium ${isUploading ? 'bg-gray-100 text-gray-400' : uploaded ? 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50' : 'bg-violet-600 text-white hover:bg-violet-700'}`}>
                        {isUploading ? 'Uploading' : uploaded ? 'Replace' : 'Upload'}
                        <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" disabled={isUploading} onChange={(e) => handleSlotUpload(docName, e)} />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="mt-4 text-sm text-gray-400">No required documents for this membership category.</p>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="text-base font-medium text-gray-900">Additional Information</h3>
          <div className="mt-4 space-y-4">
            <Field label="Qualifications">
              <textarea className={inputClass} rows={3} value={form.qualifications} onChange={(e) => set('qualifications', e.target.value)} placeholder="Educational and professional qualifications" />
            </Field>
            <Field label="Additional Notes">
              <textarea className={inputClass} rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Mention if referred by a current member" />
            </Field>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-violet-600 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Submitting' : 'Submit Application'}
          </button>
        </div>
      </form>
    </Shell>
  );
};

export default PublicMembershipApplicationForm;
